import React from 'react';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion'; 
import TopBanner from '../components/TopBanner';
import BlogSideBar from '../components/BlogSideBar';
import blogList from '../components/BlogData';

export default function Blogs() {
    const allBlogs = blogList || {};

    const server_url = process.env.REACT_APP_SERVER;
    const homeimg = '/images/home/home.jpg'; 

    const schemaData = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Lurantech | Blogs",
        "url": server_url + "/blogs",
        "description": "Lurantech's blog section brings you the latest insights on IT solutions, business strategies, software development, and more. Stay updated with trends and expert opinions.",
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": server_url + "/blogs"
        },
        "publisher": {
            "@type": "Organization",
            "name": "Lurantech",
            "logo": {
                "@type": "ImageObject",
                "url": server_url + "/images/logo.png"
            }
        },
        "blogPosts": allBlogs.map(blog => ({
            "@type": "BlogPosting",
            "headline": blog.title,
            "description": blog.description,
            "author": {
                "@type": "Person",
                "name": blog.author,
                "url": blog.authorContactUrl
            },
            "datePublished": blog.displaydate.toUTCString(),
            "url": server_url + "/blog/" + blog.title.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '').toLowerCase(),
            "image": server_url + blog.imageUrlPath,
            "mainEntityOfPage": server_url + "/blog/" + blog.title.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '').toLowerCase(),
        }))
    };

    // Animation Variant for individual blog cards
    const itemVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: { 
            opacity: 1, 
            y: 0, 
            transition: { duration: 0.8, ease: "easeOut" } 
        }
    };

    return (
        <>
            <HelmetProvider>
                <Helmet>
                    <meta property="og:title" content="Lurantech | Blogs | Industry Insights, Trends, and IT Solutions" />
                    <meta property="og:description" content="Dive into Lurantech's blogs for valuable insights, the latest IT trends, and expert advice on software development, business solutions, and much more." />
                    <meta property="og:image" content={`${server_url}${homeimg}`} />
                    <meta property="og:url" content={`${server_url}/blogs`} />
                    <meta property="og:type" content="website" />
                    <meta property="og:image:type" content="image/jpeg" />
                    <meta property="og:image:width" content="1200" />
                    <meta property="og:image:height" content="675" />
                    <meta name="geo.position" content="22.3026;73.1812" />
                    <meta name="geo.placename" content="4th Floor - Office 1, 7, Sky Complex, Laxmipura, Vadodara, Gujarat 390021" />
                    <meta name="geo.region" content="IN-GJ" />
                    <meta property="og:latitude" content="22.3026" />
                    <meta property="og:longitude" content="73.1812" />
                    <meta property="og:location" content="4th Floor - Office 1, 7, Sky Complex, Laxmipura, Vadodara, Gujarat 390021" />

                    <meta name="twitter:title" content="Lurantech | Blogs | Industry Insights, Trends, and IT Solutions" />
                    <meta name="twitter:description" content="Dive into Lurantech's blogs for valuable insights, the latest IT trends, and expert advice on software development, business solutions, and much more." />
                    <meta name="twitter:image" content={`${server_url}${homeimg}`} />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta property="twitter:image:type" content="image/jpeg" />
                    <meta property="twitter:image:width" content="1200" />
                    <meta property="twitter:image:height" content="675" />

                    <meta name="robots" content="index, follow" />
                    <meta name="description" content="Dive into Lurantech's blogs for valuable insights, the latest IT trends, and expert advice on software development, business solutions, and much more." />
                    <meta name="keywords" content="Lurantech blogs, liferay blogs, e-commorce solutions, liferay cms, liferay e-commorce, liferay india, liferay gujarat, lurantech liferay, IT solutions, business strategies, software development, industry insights, enterprise business solutions, technology trends" />
                    <meta name="author" content="Lurantech" />

                    <link rel="canonical" href={`${server_url}/blogs`} />
                    <link rel="preload" href={`${server_url}${homeimg}`} as="image" />
                    <title>Lurantech | Blogs | Industry Insights, Trends, and IT Solutions</title>
                </Helmet>

                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(schemaData),
                    }}
                />

                <TopBanner title="Explore our Expertise" bgimage="/images/blogs/blog.gif" />
                
                <div className="blog-container container">
                    <div className="row g-0">
                        {/* MAIN CONTENT COLUMN */}
                        <div className="col-12 col-sm-8 col-md-8 col-lg-8 col-xl-8">
                            {allBlogs.map((blog, index) => (
                                <motion.div 
                                    key={`blogs-${index}`} 
                                    className='s-blog-container'
                                    variants={itemVariants}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, amount: 0.2 }} // Trigger only when visible
                                >
                                    <div className='blog-img'>
                                        <img src={blog.imageUrlPath} alt={blog.title || 'Blog Image'} />
                                    </div>
                                    <div className='blog-info'>
                                        <img src="/images/blogs/author-icon.svg" alt="Lurantech author" />
                                        <div className='author-text'>{blog.author}</div>
                                        <img src="/images/blogs/clock-icon.svg" alt="Lurantech blog display date" />
                                        <div className='clock-text'>{blog.displaydate.toDateString()}</div>
                                    </div>
                                    <div className='blog-title'>
                                        <Link to={`/blog/${blog.title.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '').toLowerCase()}`} >{blog.title}</Link>
                                    </div>
                                    <div className='blog-description'>
                                        {blog.description}
                                    </div>
                                    <div className='blog-button'>
                                        <Link to={`/blog/${blog.title.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '').toLowerCase()}`} >Read More &#x2197;</Link>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                        
                        {/* SIDEBAR COLUMN */}
                        <motion.div 
                            className="col-12 col-sm-4 col-md-4 col-lg-4 col-xl-4"
                            initial={{ opacity: 0, x: 40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            <BlogSideBar blog={allBlogs[0]} />
                        </motion.div>
                    </div>
                </div>
            </HelmetProvider>
        </>
    );
}